import type { WearablePathTrack } from "./wearables.pathDefaults";
import {
  getBehaviorMemory,
  getPreferredModeWeight,
  type WearablesBehaviorMemory,
} from "./wearablesBehaviorStore";
import {
  findTrajectoryCandidate,
  getTrajectoryMemory,
} from "./wearablesTrajectoryStore";
import {
  getWearablesContext,
  type WearablesContext,
} from "./wearablesContextEngine";
import type {
  WearablesContinuityEntry,
  WearablesRouteSurface,
} from "./wearables.routeCompanion";

export type AdaptiveRecommendationReason = "signal" | "habit" | "trajectory" | "hybrid";

export type AdaptiveRecommendation = {
  mode: WearablePathTrack;
  label: string;
  destination: string;
  destinationSurface: WearablesRouteSurface;
  destinationLabel: string;
  confidence: number;
  reason: AdaptiveRecommendationReason;
  adaptation: {
    boosted: boolean;
    suppressed: boolean;
  };
  context: WearablesContext;
  narrative: {
    title: string;
    body: string;
    inlineLabel: string;
  };
};

type AdaptiveRecommendationInput = {
  mode: WearablePathTrack;
  label: string;
  destination: string;
  surface: WearablesRouteSurface;
  baseConfidence?: number;
  continuityEntry?: WearablesContinuityEntry | null;
  candidateModes?: WearablePathTrack[];
  candidateDestinations?: WearablesRouteSurface[];
};

const DESTINATION_META: Record<
  WearablesRouteSurface,
  { label: string; path: string }
> = {
  "guided-studio": {
    label: "Guided Studio",
    path: "/guided-studio",
  },
  omnistudio: {
    label: "OmniStudio",
    path: "/omnistudio",
  },
  xr: {
    label: "XR",
    path: "/guided/start-from-world",
  },
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function buildPrefix(surface: WearablesRouteSurface): string[] {
  const { recentSteps } = getTrajectoryMemory();
  const recent = recentSteps.slice(-2).map((entry) => entry.step);
  return recent.length ? recent : [`surface:${surface}`];
}

function getTimeBias(mode: WearablePathTrack, context: WearablesContext): number {
  if (context.timeOfDay === "morning" && (mode === "readiness" || mode === "recovery")) {
    return 0.08;
  }

  if (context.timeOfDay === "evening" && (mode === "creator" || mode === "xr")) {
    return 0.08;
  }

  if (context.timeOfDay === "night" && mode === "xr") {
    return 0.05;
  }

  return 0;
}

function getSessionBias(mode: WearablePathTrack, context: WearablesContext): number {
  if (context.sessionType === "xr" && mode === "xr") {
    return 0.08;
  }

  if (context.sessionType === "deep" && mode === "creator") {
    return 0.06;
  }

  if (context.sessionType === "recovery" && (mode === "recovery" || mode === "readiness")) {
    return 0.07;
  }

  return 0;
}

function getTrajectoryBias(mode: WearablePathTrack, surface: WearablesRouteSurface): {
  score: number;
  matched: boolean;
} {
  const candidate = findTrajectoryCandidate(buildPrefix(surface));
  const nextStep = candidate?.sequence[candidate.sequence.length - 1] ?? null;

  if (!nextStep) {
    return { score: 0, matched: false };
  }

  const matched = nextStep === `mode:${mode}` || nextStep === `surface:${surface}`;
  return {
    score: matched ? 0.1 : -0.04,
    matched,
  };
}

function getDefaultDestinationSurface(mode: WearablePathTrack): WearablesRouteSurface {
  switch (mode) {
    case "creator":
      return "omnistudio";
    case "xr":
      return "xr";
    case "readiness":
    case "recovery":
    case "general":
    default:
      return "guided-studio";
  }
}

function getDestinationTrajectoryBias(args: {
  currentSurface: WearablesRouteSurface;
  destinationSurface: WearablesRouteSurface;
}): { score: number; matched: boolean } {
  const candidate = findTrajectoryCandidate(buildPrefix(args.currentSurface));
  const nextStep = candidate?.sequence[candidate.sequence.length - 1] ?? null;

  if (!nextStep?.startsWith("surface:")) {
    return { score: 0, matched: false };
  }

  const matched = nextStep === `surface:${args.destinationSurface}`;
  return {
    score: matched ? 0.12 : -0.03,
    matched,
  };
}

function resolveAdaptiveDestination(args: {
  mode: WearablePathTrack;
  surface: WearablesRouteSurface;
  candidateDestinations?: WearablesRouteSurface[];
}): {
  destinationSurface: WearablesRouteSurface;
  destination: string;
  destinationLabel: string;
  trajectoryMatched: boolean;
} {
  const defaultSurface = getDefaultDestinationSurface(args.mode);
  const candidates = Array.from(
    new Set([defaultSurface, ...(args.candidateDestinations ?? [])]),
  );

  const ranked = candidates
    .map((destinationSurface) => {
      let score = destinationSurface === defaultSurface ? 0.08 : 0;
      const trajectory = getDestinationTrajectoryBias({
        currentSurface: args.surface,
        destinationSurface,
      });

      score += trajectory.score;

      if (args.mode === "creator" && destinationSurface === "omnistudio") {
        score += 0.05;
      }

      if (args.mode === "xr" && destinationSurface === "xr") {
        score += 0.05;
      }

      if (
        (args.mode === "recovery" || args.mode === "readiness") &&
        destinationSurface === "guided-studio"
      ) {
        score += 0.04;
      }

      return {
        destinationSurface,
        score,
        trajectoryMatched: trajectory.matched,
      };
    })
    .sort((left, right) => right.score - left.score);

  const selected = ranked[0] ?? {
    destinationSurface: defaultSurface,
    score: 0,
    trajectoryMatched: false,
  };

  return {
    destinationSurface: selected.destinationSurface,
    destination: DESTINATION_META[selected.destinationSurface].path,
    destinationLabel: DESTINATION_META[selected.destinationSurface].label,
    trajectoryMatched: selected.trajectoryMatched,
  };
}

function getSuppression(memory: WearablesBehaviorMemory, mode: WearablePathTrack): number {
  if (!memory.totalRecommendations) {
    return 0;
  }

  if (memory.lastAcceptedMode && memory.lastAcceptedMode !== mode && memory.acceptanceRate < 0.35) {
    return 0.05;
  }

  if (memory.overrideCount > memory.totalAcceptances && memory.preferredModes[mode] == null) {
    return 0.04;
  }

  if (memory.ignoredCount >= 2 && memory.preferredModes[mode] == null) {
    return 0.03;
  }

  return 0;
}

function buildNarrative(args: {
  mode: WearablePathTrack;
  label: string;
  surface: WearablesRouteSurface;
  reason: AdaptiveRecommendationReason;
  behaviorWeight: number;
  trajectoryMatched: boolean;
}): AdaptiveRecommendation["narrative"] {
  const destinationLabel = args.label;

  if (args.trajectoryMatched) {
    return {
      title: `${destinationLabel} looks ready next`,
      body: `This path lines up cleanly with your recent flow, so the system is keeping ${destinationLabel} close without locking you into it.`,
      inlineLabel: `${args.mode === "xr" ? "XR" : args.mode} next`,
    };
  }

  if (args.behaviorWeight >= 0.34) {
    return {
      title: `${destinationLabel} fits this session well`,
      body: `This route stays near the top because it has been landing cleanly for similar sessions and still remains fully adjustable.`,
      inlineLabel: `${args.mode === "creator" ? "Creator" : args.mode} ready`,
    };
  }

  if (args.reason === "signal") {
    return {
      title: `${destinationLabel} is ready now`,
      body: `Current readiness cues still support this route, so the system is keeping the recommendation clear and lightweight.`,
      inlineLabel: `${args.mode} now`,
    };
  }

  return {
    title: `${destinationLabel} stays in focus`,
    body: `Signal, session context, and lightweight continuity memory are lining up around this route right now.`,
    inlineLabel: `${args.mode} aligned`,
  };
}

function scoreAdaptiveRecommendation(
  input: AdaptiveRecommendationInput,
  mode: WearablePathTrack,
): AdaptiveRecommendation {
  const behavior = getBehaviorMemory();
  const destinationSelection = resolveAdaptiveDestination({
    mode,
    surface: input.surface,
    candidateDestinations: input.candidateDestinations,
  });
  const context = getWearablesContext({
    surface: input.surface,
    track: mode,
    continuityEntry: input.continuityEntry,
  });
  const behaviorWeight = getPreferredModeWeight(mode);
  const trajectory = getTrajectoryBias(mode, input.surface);

  let score = input.baseConfidence ?? 0.62;
  if (mode === input.mode) {
    score += 0.08;
  }
  score += behaviorWeight * 0.14;
  score += getTimeBias(mode, context);
  score += getSessionBias(mode, context);
  score += trajectory.score;
  score -= getSuppression(behavior, mode);

  const boosted = score > (input.baseConfidence ?? 0.62);
  const suppressed = score < (input.baseConfidence ?? 0.62);

  let reason: AdaptiveRecommendationReason = "signal";
  if (trajectory.matched && behaviorWeight > 0) {
    reason = "hybrid";
  } else if (trajectory.matched) {
    reason = "trajectory";
  } else if (behaviorWeight > 0) {
    reason = "habit";
  }

  return {
    mode,
    label: input.label,
    confidence: clamp(score, 0.28, 0.96),
    reason,
    adaptation: {
      boosted,
      suppressed,
    },
    context,
    narrative: buildNarrative({
      mode,
      label: destinationSelection.destinationLabel,
      surface: input.surface,
      reason,
      behaviorWeight,
      trajectoryMatched:
        trajectory.matched || destinationSelection.trajectoryMatched,
    }),
    destinationSurface: destinationSelection.destinationSurface,
    destination: destinationSelection.destination,
    destinationLabel: destinationSelection.destinationLabel,
  };
}

export function getAdaptiveRecommendation(
  input: AdaptiveRecommendationInput,
): AdaptiveRecommendation {
  const modes = Array.from(
    new Set([input.mode, ...(input.candidateModes ?? [])]),
  );

  const baseRecommendation = scoreAdaptiveRecommendation(input, input.mode);
  const candidates = modes.map((mode) => scoreAdaptiveRecommendation(input, mode));
  const best = candidates.sort((left, right) => right.confidence - left.confidence)[0] ?? baseRecommendation;

  if (best.mode === baseRecommendation.mode) {
    return baseRecommendation;
  }

  return best.confidence >= baseRecommendation.confidence + 0.05 ? best : baseRecommendation;
}